import React, { useState, useEffect } from 'react';
import { OwnerLayout } from '../../components/Layout';
import { useAuth } from '../../context/AuthContext';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { 
  Users, Plus, Trash2, Edit2, PawPrint, Heart, 
  UserCircle, Mail, Crown, Baby, User
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ROLE_ICONS = {
  'Parent': Crown,
  'Child': Baby,
  'Grandparent': Heart,
  'Sibling': Users,
  'Other': User,
};

const ROLE_COLORS = {
  'Parent': 'bg-navy-100 text-navy-600',
  'Child': 'bg-amber-100 text-amber-600',
  'Grandparent': 'bg-sage-100 text-sage-600',
  'Sibling': 'bg-purple-100 text-purple-600',
  'Other': 'bg-gray-100 text-gray-600',
};

export default function OwnerFamily() {
  const { user } = useAuth();
  const [familyMembers, setFamilyMembers] = useState([]);
  const [dogs, setDogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isAddingMember, setIsAddingMember] = useState(false);
  const [editingMember, setEditingMember] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    role: 'Parent',
    email: '',
    avatar_url: ''
  });

  useEffect(() => {
    fetchData();
  }, [user]);

  const fetchData = async () => {
    if (!user) return;
    
    try {
      const [membersRes, dogsRes] = await Promise.all([
        axios.get(`${API}/family/owner/${user.id}`),
        axios.get(`${API}/dogs/owner/${user.id}`)
      ]);
      setFamilyMembers(membersRes.data);
      setDogs(dogsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const payload = {
        name: formData.name,
        role: formData.role,
        email: formData.email || null,
        avatar_url: formData.avatar_url || `https://api.dicebear.com/7.x/avataaars/svg?seed=${formData.name}`
      };

      if (editingMember) {
        await axios.put(`${API}/family/${editingMember.id}`, payload);
        toast.success(`${formData.name}'s profile updated!`);
      } else {
        await axios.post(`${API}/family`, {
          owner_user_id: user.id,
          ...payload
        });
        toast.success(`Welcome to the family, ${formData.name}! 👨‍👩‍👧‍👦`);
      }
      
      setIsAddingMember(false);
      setEditingMember(null);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save family member');
    }
  };

  const resetForm = () => {
    setFormData({ name: '', role: 'Parent', email: '', avatar_url: '' });
  };

  const handleDelete = async (member) => {
    if (!window.confirm(`Are you sure you want to remove ${member.name} from the family?`)) {
      return;
    }

    try {
      await axios.delete(`${API}/family/${member.id}`);
      toast.success(`${member.name} has been removed`);
      fetchData();
    } catch (error) {
      toast.error('Failed to delete family member');
    }
  };

  const openEditDialog = (member) => {
    setEditingMember(member);
    setFormData({
      name: member.name,
      role: member.role,
      email: member.email || '',
      avatar_url: member.avatar_url || ''
    });
    setIsAddingMember(true);
  };

  const getDogsByFamilyMember = (memberId) => {
    return dogs.filter(dog => dog.assigned_family_members?.includes(memberId));
  };

  if (loading) {
    return (
      <OwnerLayout>
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="animate-pulse">
            <Users className="w-12 h-12 text-navy-300" />
          </div>
        </div>
      </OwnerLayout>
    );
  }

  return (
    <OwnerLayout>
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-heading text-3xl font-bold text-navy-600 mb-2">
            Our Family
          </h1>
          <p className="text-gray-500 font-body">
            Everyone who helps with your dog's journey
          </p>
        </div>

        <Dialog open={isAddingMember} onOpenChange={(open) => {
          setIsAddingMember(open);
          if (!open) {
            setEditingMember(null);
            resetForm();
          }
        }}>
          <DialogTrigger asChild>
            <button
              data-testid="add-family-btn"
              className="flex items-center gap-2 font-heading font-bold py-3 px-6 rounded-xl text-white
                bg-navy-600 shadow-[0_4px_0_#2a3a4d] hover:shadow-[0_2px_0_#2a3a4d] hover:translate-y-[2px] transition-all"
            >
              <Plus className="w-5 h-5" />
              Add Member
            </button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="font-heading text-2xl text-navy-600">
                {editingMember ? 'Edit Family Member' : 'Add Family Member'}
              </DialogTitle>
            </DialogHeader>
            
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label htmlFor="member-name" className="font-body font-medium">
                  Name *
                </Label>
                <Input
                  id="member-name"
                  data-testid="member-name-input"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Sarah"
                  required
                  className="h-12 rounded-xl"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="member-role" className="font-body font-medium">
                  Role in Family
                </Label>
                <Select value={formData.role} onValueChange={(value) => setFormData({ ...formData, role: value })}>
                  <SelectTrigger className="h-12 rounded-xl" data-testid="member-role-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Parent">👑 Parent</SelectItem>
                    <SelectItem value="Child">👶 Child</SelectItem>
                    <SelectItem value="Grandparent">💝 Grandparent</SelectItem>
                    <SelectItem value="Sibling">👥 Sibling</SelectItem>
                    <SelectItem value="Other">👤 Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="member-email" className="font-body font-medium">
                  Email (optional)
                </Label>
                <Input
                  id="member-email"
                  data-testid="member-email-input"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="sarah@example.com"
                  className="h-12 rounded-xl"
                />
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setIsAddingMember(false);
                    setEditingMember(null);
                    resetForm();
                  }}
                  className="flex-1 font-heading font-bold py-3 rounded-xl border-2 border-gray-200 text-gray-600 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  data-testid="save-member-btn"
                  className="flex-1 font-heading font-bold py-3 rounded-xl text-white
                    bg-navy-600 shadow-[0_4px_0_#2a3a4d] hover:shadow-[0_2px_0_#2a3a4d] hover:translate-y-[2px] transition-all"
                >
                  {editingMember ? 'Update' : 'Add Member'}
                </button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Family Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <div className="bg-white rounded-xl p-4 border border-gray-100">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-navy-100 rounded-lg flex items-center justify-center">
              <Users className="w-5 h-5 text-navy-600" />
            </div>
            <div>
              <div className="font-heading text-2xl font-bold text-navy-600">{familyMembers.length}</div>
              <div className="text-xs text-gray-500 font-body">Family Members</div>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-4 border border-gray-100">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-sage-100 rounded-lg flex items-center justify-center">
              <PawPrint className="w-5 h-5 text-sage-600" />
            </div>
            <div>
              <div className="font-heading text-2xl font-bold text-navy-600">{dogs.length}</div>
              <div className="text-xs text-gray-500 font-body">Dogs</div>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-4 border border-gray-100">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
              <Crown className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <div className="font-heading text-2xl font-bold text-navy-600">
                {familyMembers.filter(m => m.role === 'Parent').length}
              </div>
              <div className="text-xs text-gray-500 font-body">Parents</div>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-4 border border-gray-100">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
              <Baby className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <div className="font-heading text-2xl font-bold text-navy-600">
                {familyMembers.filter(m => m.role === 'Child').length}
              </div>
              <div className="text-xs text-gray-500 font-body">Kids</div>
            </div>
          </div>
        </div>
      </div>

      {/* Family Members Grid */}
      {familyMembers.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-2xl border-2 border-dashed border-gray-200">
          <div className="w-20 h-20 bg-navy-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <Users className="w-10 h-10 text-navy-400" />
          </div>
          <h2 className="font-heading text-2xl font-bold text-navy-600 mb-4">
            No Family Members Yet
          </h2>
          <p className="text-gray-500 font-body mb-6 max-w-md mx-auto">
            Add family members to assign them to dogs and track who's responsible for training!
          </p>
          <button
            onClick={() => setIsAddingMember(true)}
            className="inline-flex items-center gap-2 font-heading font-bold py-3 px-8 rounded-xl text-white
              bg-navy-600 shadow-[0_4px_0_#2a3a4d] hover:shadow-[0_2px_0_#2a3a4d] hover:translate-y-[2px] transition-all"
          >
            <Plus className="w-5 h-5" />
            Add First Member
          </button>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {familyMembers.map((member) => {
            const RoleIcon = ROLE_ICONS[member.role] || User;
            const roleColor = ROLE_COLORS[member.role] || ROLE_COLORS['Other'];
            const assignedDogs = getDogsByFamilyMember(member.id);

            return (
              <div
                key={member.id}
                data-testid={`family-card-${member.id}`}
                className="bg-white rounded-2xl overflow-hidden border border-gray-100 hover:shadow-lg transition-all"
              >
                {/* Header */}
                <div className="bg-gradient-to-r from-navy-600 to-navy-500 p-4 text-white">
                  <div className="flex items-center gap-4">
                    <img
                      src={member.avatar_url || `https://api.dicebear.com/7.x/avataaars/svg?seed=${member.name}`}
                      alt={member.name}
                      className="w-16 h-16 rounded-full bg-white border-2 border-white"
                    />
                    <div className="flex-1">
                      <h3 className="font-heading text-xl font-bold">{member.name}</h3>
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-white/20 text-white`}>
                        <RoleIcon className="w-3 h-3" />
                        {member.role}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Content */}
                <div className="p-4">
                  {/* Email */}
                  {member.email && (
                    <div className="flex items-center gap-2 text-gray-500 text-sm font-body mb-4">
                      <Mail className="w-4 h-4" />
                      {member.email}
                    </div>
                  )}

                  {/* Assigned Dogs */}
                  <div className="mb-4">
                    <h4 className="text-sm font-body font-medium text-gray-600 mb-2">Assigned Dogs</h4>
                    {assignedDogs.length > 0 ? (
                      <div className="flex flex-wrap gap-2">
                        {assignedDogs.map((dog) => (
                          <div key={dog.id} className="flex items-center gap-1.5 bg-sage-50 px-2 py-1 rounded-full">
                            {dog.photo_url ? (
                              <img src={dog.photo_url} alt={dog.name} className="w-5 h-5 rounded-full object-cover" />
                            ) : (
                              <PawPrint className="w-4 h-4 text-sage-500" />
                            )}
                            <span className="text-xs font-body text-sage-700">{dog.name}</span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-xs text-gray-400 font-body italic">No dogs assigned yet</p>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="flex gap-2">
                    <button
                      onClick={() => openEditDialog(member)}
                      data-testid={`edit-member-${member.id}`}
                      className="flex-1 flex items-center justify-center gap-2 py-2 rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-600 font-body text-sm transition-colors"
                    >
                      <Edit2 className="w-4 h-4" />
                      Edit
                    </button>
                    <button
                      onClick={() => handleDelete(member)}
                      data-testid={`delete-member-${member.id}`}
                      className="px-4 py-2 rounded-lg bg-red-50 hover:bg-red-100 text-red-500 transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Dogs & Their Trainers */}
      {dogs.length > 0 && (
        <div className="mt-12">
          <h2 className="font-heading text-2xl font-bold text-navy-600 mb-6">
            Dog Training Assignments
          </h2>
          <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
            <div className="divide-y divide-gray-100">
              {dogs.map((dog) => {
                const trainers = familyMembers.filter(m => 
                  dog.assigned_family_members?.includes(m.id)
                );

                return (
                  <div key={dog.id} className="p-4 flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      {dog.photo_url ? (
                        <img src={dog.photo_url} alt={dog.name} className="w-12 h-12 rounded-xl object-cover" />
                      ) : (
                        <div className="w-12 h-12 bg-sage-100 rounded-xl flex items-center justify-center">
                          <PawPrint className="w-6 h-6 text-sage-500" />
                        </div>
                      )}
                      <div>
                        <h3 className="font-heading font-bold text-navy-600">{dog.name}</h3>
                        <p className="text-sm text-gray-500 font-body">{dog.breed}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {trainers.length > 0 ? (
                        <div className="flex -space-x-2">
                          {trainers.map((trainer) => (
                            <img
                              key={trainer.id}
                              src={trainer.avatar_url || `https://api.dicebear.com/7.x/avataaars/svg?seed=${trainer.name}`}
                              alt={trainer.name}
                              title={trainer.name}
                              className="w-8 h-8 rounded-full border-2 border-white bg-white"
                            />
                          ))}
                        </div>
                      ) : (
                        <span className="text-sm text-gray-400 font-body italic">No trainers assigned</span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </OwnerLayout>
  );
}
